import { create } from 'zustand'
import type { SystemStatusDTO, UserSettingsDTO } from '@shared/types'
import { api, errorMessage } from '@/lib/api'
import { adoptRemoteTheme } from '@/stores/useThemeStore'

interface SettingsState {
  settings: UserSettingsDTO | null
  status: SystemStatusDTO | null
  loading: boolean
  statusLoading: boolean
  error: string | null

  load: () => Promise<void>
  save: (patch: Partial<UserSettingsDTO>) => Promise<void>
  loadStatus: () => Promise<void>
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: null,
  status: null,
  loading: false,
  statusLoading: false,
  error: null,

  load: async () => {
    set({ loading: true, error: null })
    try {
      const settings = await api.get<UserSettingsDTO>('/settings')
      set({ settings, loading: false })
      // Second device, no local choice yet — take the account's preference.
      adoptRemoteTheme(settings.theme)
    } catch (error) {
      set({ loading: false, error: errorMessage(error) })
    }
  },

  save: async (patch) => {
    const previous = get().settings
    if (previous) set({ settings: { ...previous, ...patch } })
    try {
      const settings = await api.patch<UserSettingsDTO>('/settings', patch)
      set({ settings, error: null })
    } catch (error) {
      set({ settings: previous, error: errorMessage(error) })
      throw error
    }
  },

  loadStatus: async () => {
    set({ statusLoading: true })
    try {
      const status = await api.get<SystemStatusDTO>('/system/status')
      set({ status, statusLoading: false })
    } catch (error) {
      set({ statusLoading: false, error: errorMessage(error) })
    }
  }
}))
